/**
 * CR001: Spend & Earn Offer Card
 * Display card for spend-to-earn promotions
 */

import { useState } from "react";
import { ExternalLink, Gift, DollarSign, Coins, Calendar, Users, ShoppingBag, Tag, AlertCircle, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";
import { getMerchantLogo } from "@/lib/brandAssets";
import {
  SpendEarnOffer,
  getRewardFormLabel,
  getCategoryLabel,
  computeOfferStatus,
  formatOfferValidity,
} from "@/lib/types/spendEarnOffers";

interface SpendEarnOfferCardProps {
  offer: SpendEarnOffer;
  isAdmin?: boolean;
}

function getRewardIcon(form: SpendEarnOffer['reward_form']) {
  switch (form) {
    case 'GIFT_CARD': return Gift;
    case 'CASH': return DollarSign;
    case 'POINTS': return Coins;
    default: return Tag;
  }
}

function getRewardColor(form: SpendEarnOffer['reward_form']) {
  switch (form) {
    case 'GIFT_CARD': return 'bg-blue-600';
    case 'CASH': return 'bg-green-600';
    case 'POINTS': return 'bg-purple-600';
    default: return 'bg-gray-600';
  }
}

function getStatusBadge(status: 'active' | 'upcoming' | 'expired') {
  switch (status) {
    case 'active':
      return <Badge className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">Active</Badge>;
    case 'upcoming':
      return <Badge className="bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">Upcoming</Badge>;
    case 'expired':
      return <Badge className="bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200">Expired</Badge>;
  }
}

export function SpendEarnOfferCard({ offer, isAdmin = false }: SpendEarnOfferCardProps) {
  const [logoError, setLogoError] = useState(false);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  
  const RewardIcon = getRewardIcon(offer.reward_form);
  const rewardColor = getRewardColor(offer.reward_form);
  const status = computeOfferStatus(offer);
  const merchantLogo = getMerchantLogo(offer.merchant_name);
  
  // Build value display
  const valueDisplay = offer.computed_value_cad 
    ? `$${Math.round(offer.computed_value_cad)}`
    : offer.effective_percent_back 
      ? `${offer.effective_percent_back}%`
      : offer.discount_percent
        ? `${offer.discount_percent}%`
        : offer.points_amount
          ? `${offer.points_amount.toLocaleString()} pts`
          : null;

  // URLs for buttons
  const viewOfferUrl = offer.view_offer_url || offer.deal_link || offer.source_url;
  const termsUrl = offer.terms_url || offer.detail_link;
  
  return (
    <div className={cn(
      "relative rounded-xl overflow-hidden shadow-lg bg-card border transition-all hover:shadow-xl",
      status === 'expired' && "opacity-60"
    )}>
      {/* Header with merchant */}
      <div className="h-20 bg-gradient-to-r from-black/90 to-black/70 flex items-center px-6 pr-32 relative">
        {merchantLogo && !logoError ? (
          <img 
            src={merchantLogo}
            alt={offer.merchant_name}
            className="h-10 object-contain max-w-[180px]"
            onError={() => setLogoError(true)}
          />
        ) : (
          <span className="text-white text-lg font-semibold truncate max-w-[180px]">
            {offer.merchant_name}
          </span>
        )}
        
        {/* Corner ribbon */}
        <div className={cn(
          "absolute top-0 right-0 w-32 h-32",
          rewardColor
        )} style={{
          clipPath: "polygon(100% 0, 100% 100%, 0 0)"
        }}>
          <div className="absolute top-3 right-3 text-right text-white">
            <span className="text-xs uppercase tracking-wide opacity-90 block">
              {getRewardFormLabel(offer.reward_form)}
            </span>
            {valueDisplay && (
              <span className="text-lg font-bold block">{valueDisplay}</span>
            )}
          </div>
        </div>
      </div>
      
      {/* Content area */}
      <div className="p-5 space-y-4">
        {/* Status + Category badges */}
        <div className="flex items-center gap-2 flex-wrap">
          {getStatusBadge(status)}
          <Badge variant="outline">{getCategoryLabel(offer.offer_category)}</Badge>
          {offer.audience_hint === 'corporate_bulk' && isAdmin && (
            <Badge variant="secondary" className="bg-amber-100 text-amber-800">
              <Users className="h-3 w-3 mr-1" />
              Corporate
            </Badge>
          )}
        </div>
        
        {/* Headline */}
        <h3 className="font-semibold text-lg leading-tight line-clamp-2">
          {offer.headline_mechanic}
        </h3>
        
        {/* Key details */}
        <div className="space-y-2 text-sm">
          {/* Qualifying spend */}
          {offer.qualifying_spend_cad && (
            <div className="flex items-center gap-2 text-muted-foreground">
              <ShoppingBag className="h-4 w-4 flex-shrink-0" />
              <span>Spend ${offer.qualifying_spend_cad.toLocaleString()}</span>
            </div>
          )}
          
          {/* Reward details */}
          <div className="flex items-center gap-2 text-muted-foreground">
            <RewardIcon className="h-4 w-4 flex-shrink-0" />
            <span>
              {offer.reward_form === 'GIFT_CARD' && 'Get gift card reward'}
              {offer.reward_form === 'CASH' && 'Get cash back'}
              {offer.reward_form === 'POINTS' && offer.points_program_name 
                ? `Earn ${offer.points_program_name}` 
                : 'Earn points'}
              {offer.max_reward_cad && ` (up to $${offer.max_reward_cad})`}
            </span>
          </div>
          
          {/* Membership required */}
          {offer.membership_required && (
            <div className="flex items-center gap-2 text-amber-600 dark:text-amber-400">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              <span className="text-xs">{offer.membership_required} required</span>
            </div>
          )}
          
          {/* Dates */}
          <div className="flex items-center gap-2 text-muted-foreground">
            <Calendar className="h-4 w-4 flex-shrink-0" />
            <span className="text-xs">{formatOfferValidity(offer)}</span>
          </div>
        </div>
        
        {/* Expandable details */}
        {(offer.product_scope || offer.exclusions || offer.fine_print) && (
          <Collapsible open={isDetailsOpen} onOpenChange={setIsDetailsOpen}>
            <CollapsibleTrigger asChild>
              <Button variant="ghost" size="sm" className="w-full justify-between text-muted-foreground">
                <span>Details & Exclusions</span>
                <ChevronDown className={cn(
                  "h-4 w-4 transition-transform",
                  isDetailsOpen && "rotate-180"
                )} />
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="pt-2 space-y-2 text-sm text-muted-foreground">
              {offer.product_scope && (
                <div>
                  <span className="font-medium text-foreground">Applies to: </span>
                  {offer.product_scope}
                </div>
              )}
              {offer.exclusions && (
                <div>
                  <span className="font-medium text-foreground">Exclusions: </span>
                  {offer.exclusions}
                </div>
              )}
              {offer.fine_print && (
                <div className="text-xs opacity-75">{offer.fine_print}</div>
              )}
            </CollapsibleContent>
          </Collapsible>
        )}
        
        {/* Action buttons */}
        <div className="flex gap-2 pt-2">
          {viewOfferUrl && (
            <Button asChild className="flex-1" size="sm">
              <a href={viewOfferUrl} target="_blank" rel="noopener noreferrer">
                <ExternalLink className="h-4 w-4 mr-2" />
                View Offer
              </a>
            </Button>
          )}
          {termsUrl && termsUrl !== viewOfferUrl && (
            <Button asChild variant="outline" size="sm">
              <a href={termsUrl} target="_blank" rel="noopener noreferrer">
                T&Cs
              </a>
            </Button>
          )}
          {offer.claim_url && (
            <Button asChild variant="secondary" size="sm">
              <a href={offer.claim_url} target="_blank" rel="noopener noreferrer">
                Claim
              </a>
            </Button>
          )}
        </div>
        
        {/* Admin debug info */}
        {isAdmin && (
          <div className="pt-2 border-t text-xs text-muted-foreground space-y-1">
            <div>Confidence: {offer.confidence ? `${(offer.confidence * 100).toFixed(0)}%` : 'N/A'}</div>
            <div>Value Score: {offer.normalized_value_score?.toFixed(2) || 'N/A'}</div>
            {offer.run_id && <div className="truncate">Run: {offer.run_id}</div>}
          </div>
        )}
      </div>
    </div>
  );
}
